import clsx from 'clsx';
import { capitalize } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHoverDirty, useToggle } from 'react-use';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Stack, Tooltip } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';

import * as styles from './AutoPricingCompListingRowOffset.css';

enum CompListingRowOffset {
  MyRow = 'MyRow',
  MinusOne = 'MinusOne',
  MinusThree = 'MinusThree',
  MinusSix = 'MinusSix',
  Custom = 'Custom',
}

const CompListingRowOffsetToValue = {
  [CompListingRowOffset.MyRow]: 0,
  [CompListingRowOffset.MinusOne]: 1,
  [CompListingRowOffset.MinusThree]: 3,
  [CompListingRowOffset.MinusSix]: 6,
  [CompListingRowOffset.Custom]: undefined,
};

const getOffsetTypeFromOffsetValue = (offset: number | undefined | null) => {
  if (offset === 0) return CompListingRowOffset.MyRow;
  if (offset === 1) return CompListingRowOffset.MinusOne;
  if (offset === 3) return CompListingRowOffset.MinusThree;
  if (offset === 6) return CompListingRowOffset.MinusSix;
  return CompListingRowOffset.Custom;
};

type AutoPricingCompListingRowOffsetProps = {
  listingId?: number | undefined;
  rowOffset: number | undefined | null;
  onRowOffsetChange: (rowOffset: number | undefined) => void;
};

export const AutoPricingCompListingRowOffset = ({
  listingId,
  rowOffset,
  onRowOffsetChange,
}: AutoPricingCompListingRowOffsetProps) => {
  const myRowText = useContent(ContentId.RowCompSettingExactMatch);
  const [customEditing, setCustomEditing] = useToggle(false);

  const offsetEnum = useMemo(
    () => getOffsetTypeFromOffsetValue(rowOffset),
    [rowOffset]
  );

  const toggleOptions = useMemo(() => {
    return [
      {
        value: CompListingRowOffset.MyRow,
        children: <span>{capitalize(myRowText)}</span>,
      },
      {
        value: CompListingRowOffset.MinusOne,
        children: '-1',
      },
      {
        value: CompListingRowOffset.MinusThree,
        children: '-3',
      },
      {
        value: CompListingRowOffset.MinusSix,
        children: '-6',
      },
      {
        value: CompListingRowOffset.Custom,
        children: (
          <CustomRowOffset
            listingId={listingId}
            rowOffset={rowOffset ?? undefined}
            onRowOffsetChange={(rowOffset) => {
              onRowOffsetChange(rowOffset);
              setCustomEditing(false);
            }}
            selected={offsetEnum === CompListingRowOffset.Custom}
            editing={customEditing}
          />
        ),
      },
    ];
  }, [
    listingId,
    customEditing,
    myRowText,
    offsetEnum,
    onRowOffsetChange,
    rowOffset,
    setCustomEditing,
  ]);

  const onUpdateRowOffset = useCallback(
    (v: CompListingRowOffset) => {
      if (v === CompListingRowOffset.Custom) {
        setCustomEditing(false);
        setTimeout(() => {
          setCustomEditing(true);
        }, 50);
      }
      if (v === offsetEnum) {
        if (v !== CompListingRowOffset.Custom) {
          onRowOffsetChange(undefined);
        }
      } else {
        onRowOffsetChange(
          CompListingRowOffsetToValue[v as CompListingRowOffset]
        );
      }
    },
    [offsetEnum, setCustomEditing, onRowOffsetChange]
  );

  return (
    <Stack alignItems="center" className={styles.offsetOptions}>
      {toggleOptions.map(({ value, children }) => (
        <div
          key={value}
          onClick={() => onUpdateRowOffset(value)}
          className={clsx(styles.rowOffsetItem, {
            [styles.rowOffsetItemSelected]: offsetEnum === value,
            [styles.customContainer]: value === CompListingRowOffset.Custom,
          })}
        >
          {children}
        </div>
      ))}
    </Stack>
  );
};

type CustomRowOffsetProps = {
  listingId: number | undefined; // Used to reset the local row offset when switching listings
  selected: boolean;
  editing: boolean; // Mainly used to initialize the state so that we show custom text when initially selected
  rowOffset: number | undefined;
  onRowOffsetChange: (rowOffset: number | undefined) => void;
};

const CustomRowOffset = ({
  listingId,
  selected,
  editing,
  rowOffset,
  onRowOffsetChange,
}: CustomRowOffsetProps) => {
  const elementRef = useRef<HTMLInputElement>(null);
  const isHovering = useHoverDirty(elementRef);
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState<boolean>(false);

  const { isDarkMode } = useSiteTheme();

  const customText = useContent(ContentId.Custom);
  const [localRowOffset, setLocalRowOffset] = useState<number | undefined>(
    () =>
      !rowOffset || [0, 1, 3, 6].indexOf(rowOffset) ? undefined : rowOffset
  );

  useEffect(() => {
    if (listingId) {
      setLocalRowOffset(
        !rowOffset || [0, 1, 3, 6].indexOf(rowOffset) ? undefined : rowOffset
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId]);

  useEffect(() => {
    if (editing) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    }
  }, [editing]);

  const shouldShowCustomText = useMemo(() => {
    if (localRowOffset != null) {
      return false;
    }
    return rowOffset == null || [0, 1, 3, 6].indexOf(rowOffset) > -1;
  }, [localRowOffset, rowOffset]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (inputRef.current) {
          inputRef.current.blur();
        }
      }
    },
    []
  );

  return (
    <Tooltip
      open={isHovering}
      delayDuration={100}
      contentVariant={isDarkMode ? 'light' : 'dark'}
      align="center"
      triggerContent={
        <div ref={elementRef} onClick={() => setFocused(true)}>
          {!selected || !focused ? (
            <div style={{ padding: `${vars.spacing.sm} ${vars.spacing.m}` }}>
              {shouldShowCustomText
                ? customText
                : `-${localRowOffset ?? rowOffset}`}
            </div>
          ) : (
            <PosTextField
              value={localRowOffset}
              type="number"
              ref={inputRef}
              rootProps={{ className: styles.customInput }}
              onChange={(e) => {
                const v = parseInt(e.target.value);
                e.stopPropagation();
                setLocalRowOffset(v);
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => setFocused(true)}
              onBlur={() => {
                onRowOffsetChange(localRowOffset);
                setFocused(false);
              }}
              prefixDisplay={'-'}
            />
          )}
        </div>
      }
    >
      <Content id={ContentId.Custom} />
    </Tooltip>
  );
};
